/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { cn } from '@repo/shared-client/lib/utils'
import { useLayoutEffect, useRef } from 'react'
import { applyPhotoFilter, blurCanvas, supportsCanvasFilters } from './image-editor-filters'
import {
  type CoverKind,
  getCoverBlurPx,
  getCoverEffectFilter,
  getCoverPixelCellPx,
  getPhotoFilterCss,
  type PhotoFilter,
} from './image-editor-types'

const BLUR_SAMPLE_SIGMAS = 3

type ImageEditorCoverPreviewProps = {
  clipClass: string
  coverHeight: number
  coverLeft: number
  coverRotation: number
  coverTop: number
  coverWidth: number
  displayUrl: string
  isSelected: boolean
  kind: Exclude<CoverKind, 'emoji'>
  orientedDisplayHeight: number
  orientedDisplayWidth: number
  photoFilter: PhotoFilter
  photoOffsetX: number
  photoOffsetY: number
  viewHeight: number
  viewWidth: number
}

const drawCoverEffectPreview = ({
  canvas,
  image,
  coverHeight,
  coverLeft,
  coverRotation,
  coverTop,
  coverWidth,
  kind,
  orientedDisplayHeight,
  orientedDisplayWidth,
  photoFilter,
  photoOffsetX,
  photoOffsetY,
  viewHeight,
  viewWidth,
}: {
  canvas: HTMLCanvasElement
  image: HTMLImageElement
  coverHeight: number
  coverLeft: number
  coverRotation: number
  coverTop: number
  coverWidth: number
  kind: Exclude<CoverKind, 'emoji'>
  orientedDisplayHeight: number
  orientedDisplayWidth: number
  photoFilter: PhotoFilter
  photoOffsetX: number
  photoOffsetY: number
  viewHeight: number
  viewWidth: number
}) => {
  if (
    viewWidth <= 0 ||
    viewHeight <= 0 ||
    orientedDisplayWidth <= 0 ||
    orientedDisplayHeight <= 0 ||
    image.naturalWidth <= 0 ||
    image.naturalHeight <= 0 ||
    coverWidth <= 0 ||
    coverHeight <= 0
  ) {
    return
  }

  canvas.width = Math.max(1, Math.round(coverWidth))
  canvas.height = Math.max(1, Math.round(coverHeight))
  const context = canvas.getContext('2d')
  if (!context) {
    return
  }

  context.setTransform(1, 0, 0, 1, 0, 0)
  context.clearRect(0, 0, canvas.width, canvas.height)

  const sourceX = ((0 - photoOffsetX) / orientedDisplayWidth) * image.naturalWidth
  const sourceY = ((0 - photoOffsetY) / orientedDisplayHeight) * image.naturalHeight
  const sourceWidth = (viewWidth / orientedDisplayWidth) * image.naturalWidth
  const sourceHeight = (viewHeight / orientedDisplayHeight) * image.naturalHeight
  const photoFilterCss = getPhotoFilterCss(photoFilter)
  const nativeFilters = supportsCanvasFilters()

  const orientCover = (target: CanvasRenderingContext2D, originX: number, originY: number) => {
    target.translate(originX, originY)
    target.rotate((-coverRotation * Math.PI) / 180)
    target.translate(-canvas.width / 2, -canvas.height / 2)
  }

  try {
    if (kind === 'pixelate') {
      const cell = getCoverPixelCellPx(coverWidth, coverHeight)
      const smallWidth = Math.max(1, Math.round(viewWidth / cell))
      const smallHeight = Math.max(1, Math.round(viewHeight / cell))
      const small = document.createElement('canvas')
      small.width = smallWidth
      small.height = smallHeight
      const smallContext = small.getContext('2d', nativeFilters ? undefined : { willReadFrequently: true })
      if (!smallContext) {
        return
      }
      if (nativeFilters) {
        smallContext.filter = photoFilterCss
      }
      smallContext.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, smallWidth, smallHeight)
      if (nativeFilters) {
        smallContext.filter = 'none'
      } else {
        applyPhotoFilter(smallContext, photoFilter)
      }
      orientCover(context, canvas.width / 2, canvas.height / 2)
      context.imageSmoothingEnabled = false
      context.drawImage(small, -coverLeft, -coverTop, viewWidth, viewHeight)
      context.imageSmoothingEnabled = true
      return
    }

    const blurPx = getCoverBlurPx(coverWidth, coverHeight)
    if (nativeFilters) {
      orientCover(context, canvas.width / 2, canvas.height / 2)
      context.filter = getCoverEffectFilter(photoFilterCss, blurPx)
      context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        -coverLeft,
        -coverTop,
        viewWidth,
        viewHeight
      )
      context.filter = 'none'
      return
    }

    const pad = Math.max(1, Math.ceil(blurPx * BLUR_SAMPLE_SIGMAS))
    const padded = document.createElement('canvas')
    padded.width = canvas.width + pad * 2
    padded.height = canvas.height + pad * 2
    const paddedContext = padded.getContext('2d', { willReadFrequently: true })
    if (!paddedContext) {
      return
    }
    orientCover(paddedContext, pad + canvas.width / 2, pad + canvas.height / 2)
    paddedContext.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      -coverLeft,
      -coverTop,
      viewWidth,
      viewHeight
    )
    applyPhotoFilter(paddedContext, photoFilter)
    blurCanvas(padded, blurPx)
    context.drawImage(padded, pad, pad, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height)
  } catch {
    return
  }
}

const ImageEditorCoverPreview = ({
  clipClass,
  coverHeight,
  coverLeft,
  coverRotation,
  coverTop,
  coverWidth,
  displayUrl,
  isSelected,
  kind,
  orientedDisplayHeight,
  orientedDisplayWidth,
  photoFilter,
  photoOffsetX,
  photoOffsetY,
  viewHeight,
  viewWidth,
}: ImageEditorCoverPreviewProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }

    let cancelled = false
    const image = new window.Image()
    if (displayUrl.startsWith('http://') || displayUrl.startsWith('https://')) {
      image.crossOrigin = 'anonymous'
    }

    const paint = () => {
      if (cancelled || !image.complete || image.naturalWidth <= 0) {
        return
      }

      drawCoverEffectPreview({
        canvas,
        image,
        coverHeight,
        coverLeft,
        coverRotation,
        coverTop,
        coverWidth,
        kind,
        orientedDisplayHeight,
        orientedDisplayWidth,
        photoFilter,
        photoOffsetX,
        photoOffsetY,
        viewHeight,
        viewWidth,
      })
    }

    image.addEventListener('load', paint)
    image.src = displayUrl
    if (image.complete) {
      paint()
    }

    return () => {
      cancelled = true
      image.removeEventListener('load', paint)
    }
  }, [
    coverHeight,
    coverLeft,
    coverRotation,
    coverTop,
    coverWidth,
    displayUrl,
    kind,
    orientedDisplayHeight,
    orientedDisplayWidth,
    photoFilter,
    photoOffsetX,
    photoOffsetY,
    viewHeight,
    viewWidth,
  ])

  return (
    <div
      className={cn(
        'size-full overflow-hidden',
        clipClass,
        isSelected && 'outline outline-2 outline-primary outline-offset-1'
      )}
    >
      <canvas
        className="block size-full"
        ref={canvasRef}
        style={{ imageRendering: kind === 'pixelate' ? 'pixelated' : undefined }}
      />
    </div>
  )
}

export default ImageEditorCoverPreview
